import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AlertCircle, ChevronDown, RefreshCw } from 'lucide-react';
import { usePartialDepositOverview } from '@/hooks/usePartialDepositOverview';
import type { PartialDepositEntry } from '@/supabase/deposit/types/deposit';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const won = (n: number) => n.toLocaleString('ko-KR');
const summarize = (names: string[]) =>
  names.length === 0 ? '' : names.length === 1 ? names[0] : `${names[0]} 외 ${names.length - 1}건`;

// P0002/P0003 DETAIL 토큰 → 안내 카피(convert §16.8 + settle §16.7 공용). 용어 하드룰: "완납"·"잔금" 금지.
const FAIL_COPY: Record<string, string> = {
  already_shipped: '이미 출고 완료된 주문입니다.',
  already_partial: '이미 부분입금 상태입니다.',
  already_paid: '이미 입금 완료 상태입니다.',
  not_paid: '입금 완료 상태가 아닙니다.',
  not_partial: '부분입금 상태가 아닙니다.',
  not_eligible: '자동 처리 대상이 아닙니다.',
  not_linked: '계좌 연동이 필요합니다.',
  fingerprint_mismatch: '주문 정보가 바뀌었습니다.\n새로고침 후 다시 확인합니다.',
  snapshot_mismatch: '주문 정보가 바뀌었습니다.\n새로고침 후 다시 확인합니다.',
  settings_changed: '배송비 설정이 바뀌었습니다.\n새로고침 후 다시 확인합니다.',
  no_longer_underpaid: '확정된 입금이 정산서 금액에 도달했습니다.',
  not_found: '주문을 찾을 수 없습니다.',
  stale: '주문 정보가 바뀌었습니다.\n새로고침 후 다시 확인합니다.',
  error: '처리에 실패했습니다.',
};

type Tier = 'settleReady' | 'safetyLane' | 'inProgress';

// 진행률 pill(§16.1-2 "29,500 / 41,500원"): settleReady = emerald(전액 도착), 나머지 = amber(미수).
const PROGRESS_PILL: Record<Tier, string> = {
  settleReady:
    'ml-auto shrink-0 whitespace-nowrap rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold tabular-nums text-emerald-800',
  safetyLane:
    'ml-auto shrink-0 whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold tabular-nums text-amber-800',
  inProgress:
    'ml-auto shrink-0 whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold tabular-nums text-amber-800',
};

export function PartialDepositSection({ userId, accountLinked, queueVersion, onRequestRefresh }: {
  userId: string | null;
  accountLinked: boolean;
  queueVersion: number;                   // 기존 입금 큐 변경 신호(confirm/undo) — 두 데이터 섬 무효화 (codexloop PR C R1-P1-2)
  onRequestRefresh: () => void;           // 전환/마감 성공 시 기존 큐 재조회 요청 — DepositRow onRequestRefresh 관례
}) {
  const { groups, loading, error, refresh, convert, settle } = usePartialDepositOverview(userId);
  // 행별 실패 사유(스펙 §5) — refresh로 행이 사라져도 결과가 보이도록 닉네임 스냅샷과 함께 groups와 독립 보존
  const [failReport, setFailReport] = useState<{ nickname: string; reason: string }[]>([]);
  // settle 미달 확인 다이얼로그(§16.7 2단계) — 서버 반환 confirmed_sum/paid_threshold를 함께 스냅샷
  const [settleTarget, setSettleTarget] = useState<{
    entry: PartialDepositEntry; confirmedSum: number; paidThreshold: number;
  } | null>(null);
  // 주문 상세 인라인 펼침(§16.9 정산서 인라인) — 행별 토글
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  // mutation 진행 가드 — RPC 대기 중 더블 클릭이 already_partial 거짓 실패 보고를 만드는 것 방지 (R1-P2-3)
  const [busy, setBusy] = useState(false);
  // key=userId 리마운트 후에도 살아남는 고아 핸들러의 toast·onRequestRefresh 실행 차단 (codexloop PR C R2-P1-4).
  // setup에서 true 복구 필수 — cleanup 단독이면 StrictMode의 setup→cleanup→setup 재실행에서
  // 영구 false가 되어 mutation 피드백이 전멸한다 (R3-P1). useLayoutEffect = 동기 언마운트 fence.
  const aliveRef = useRef(true);
  useLayoutEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);
  // mount 시점 queueVersion 기준선 — 리마운트가 물려받는 >0 값의 중복 overview 조회 방지.
  // 초기 로드 0→1 범프는 섹션이 큐보다 먼저 마운트되면 기준선이 0이라 여전히 1회 잉여 재조회를
  // 만든다(정합성 무결 — sequence guard가 구요청 커밋 폐기, 수용된 P2. codexloop PR C R3).
  const seenQueueVersionRef = useRef(queueVersion);

  useEffect(() => {                       // 유저 전환 시 로컬 상태 초기화 (이전 셀러 닉네임 보고·선택 잔존 방지)
    setFailReport([]);
    setSettleTarget(null);
    setExpanded(new Set());
  }, [userId]);

  useEffect(() => {                       // 기존 큐 confirm/undo → overview 재조회
    if (seenQueueVersionRef.current !== queueVersion) {
      seenQueueVersionRef.current = queueVersion;
      void refresh();
    }
  }, [queueVersion, refresh]);

  if (!userId) return null;
  const rows: { entry: PartialDepositEntry; tier: Tier }[] = groups
    ? [
        ...groups.settleReady.map((entry) => ({ entry, tier: 'settleReady' as Tier })),
        ...groups.safetyLane.map((entry) => ({ entry, tier: 'safetyLane' as Tier })),
        ...groups.inProgress.map((entry) => ({ entry, tier: 'inProgress' as Tier })),
      ]
    : [];
  const total = rows.length;
  if (!accountLinked && total === 0) return null;   // 게이트: 미연동 + 데이터 없음 = 미노출 (데이터는 정직하게)
  // 상단 요약(★오너 2026-07-25): 총 미수 금액 한눈에 — remaining>0 행 합(전액 도착 행은 0 기여)
  const remainingTotal = rows.reduce((sum, r) => sum + Math.max(0, r.entry.remaining), 0);

  const toggleDetail = (id: number) =>
    setExpanded((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

  // 비계약 오류(P0001·네트워크)는 훅이 rethrow — catch로 토스트+실패 보고 (codexloop PR C R1-P1-5)
  // await 이후 aliveRef 확인 — 리마운트로 고아가 된 핸들러의 toast·onRequestRefresh가
  // 새 유저 화면·공유 sequence를 오염시키는 것 차단 (R2-P1-4)
  const onConvert = async (entry: PartialDepositEntry) => {
    if (busy || error) return;
    setBusy(true);
    try {
      const r = await convert(entry);
      if (!aliveRef.current) return;
      if (r.ok) {
        setFailReport([]);
        toast.success('부분입금으로 전환했습니다.');
        onRequestRefresh();
      } else {
        setFailReport([{ nickname: entry.nickname, reason: r.reason ?? 'error' }]);
        toast.error(FAIL_COPY[r.reason ?? 'error'] ?? FAIL_COPY.error);
      }
    } catch {
      if (!aliveRef.current) return;
      setFailReport([{ nickname: entry.nickname, reason: 'error' }]);
      toast.error(FAIL_COPY.error);
    } finally {
      setBusy(false);
    }
  };

  // settle 1단계(ack=false): 충분하면 즉시 마감(1클릭), 서버가 미달 판정하면 requires_manual_override
  // 구조화 반환 → 실수 방지 확인 다이얼로그(§16.7). 판정은 항상 서버 재계산(클라 remaining 아님).
  const onSettle = async (entry: PartialDepositEntry) => {
    if (busy || error) return;
    setBusy(true);
    try {
      const r = await settle(entry, false);
      if (!aliveRef.current) return;
      if (r.ok) {
        setFailReport([]);
        toast.success('입금 완료로 처리했습니다.');
        onRequestRefresh();
      } else if (r.reason === 'requires_manual_override') {
        setSettleTarget({
          entry,
          confirmedSum: r.confirmedSum ?? entry.confirmedSum,
          paidThreshold: r.paidThreshold ?? entry.paidThreshold,
        });
      } else {
        setFailReport([{ nickname: entry.nickname, reason: r.reason ?? 'error' }]);
        toast.error(FAIL_COPY[r.reason ?? 'error'] ?? FAIL_COPY.error);
      }
    } catch {
      if (!aliveRef.current) return;
      setFailReport([{ nickname: entry.nickname, reason: 'error' }]);
      toast.error(FAIL_COPY.error);
    } finally {
      setBusy(false);
    }
  };

  // settle 2단계(ack=true): 확인 다이얼로그 통과 후 재시도. CAS는 entry 스냅샷 기준 —
  // 사이에 주문이 바뀌면 P0003 stale로 거부(조용한 미달 마감 차단).
  const onSettleAck = async () => {
    if (!settleTarget || busy || error) return;
    setBusy(true);
    try {
      const r = await settle(settleTarget.entry, true);
      if (!aliveRef.current) return;
      if (r.ok) {
        setFailReport([]);
        toast.success('입금 완료로 처리했습니다.');
        onRequestRefresh();
      } else {
        // ack=true에서 ok:false는 계약상 없음 — 방어적으로 실패 보고
        setFailReport([{ nickname: settleTarget.entry.nickname, reason: r.reason ?? 'error' }]);
        toast.error(FAIL_COPY[r.reason ?? 'error'] ?? FAIL_COPY.error);
      }
    } catch {
      if (!aliveRef.current) return;
      setFailReport([{ nickname: settleTarget.entry.nickname, reason: 'error' }]);
      toast.error(FAIL_COPY.error);
    } finally {
      setSettleTarget(null);
      setBusy(false);
    }
  };

  return (
    <section className="space-y-3">
      {/* 섹션 헤더 — 확인 대기 헤더와 동일 규격(15px bold). 요약(N건·남은 금액 합)은 헤더 인라인, 새로고침 우측. */}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <h2 className="text-[15px] font-bold text-slate-900">부분입금</h2>
        {groups && total > 0 && (
          <span className="text-[13px] tabular-nums text-slate-500">
            {total}건 · 남은 금액 <span className="font-semibold text-amber-700">{won(remainingTotal)}원</span>
          </span>
        )}
        <Button
          variant="outline"
          size="sm"
          className="ml-auto h-8 gap-1.5 border-slate-200 px-2.5 text-xs font-medium text-slate-500 shadow-none hover:bg-slate-50 hover:text-slate-700 [&_svg]:size-3.5"
          onClick={() => void refresh()}
          disabled={loading}
        >
          <RefreshCw className={loading ? 'animate-spin' : undefined} aria-hidden />
          새로고침
        </Button>
      </div>
      {error && (
        <div className="rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-red-200">
          <p className="break-keep text-sm text-red-600">목록을 불러오지 못했습니다.<br />새로고침으로 다시 시도합니다.</p>
        </div>
      )}
      {!error && groups && total === 0 && (
        <p className="whitespace-pre-line rounded-xl border border-dashed border-slate-200 bg-white/50 px-4 py-5 text-center text-[13px] leading-relaxed text-slate-400">
          {'부분입금 주문이 없습니다.\n입금 완료 후 추가 구매가 생기면 여기 자동으로 모입니다.'}
        </p>
      )}

      {failReport.length > 0 && (   /* 섹션 최상위 — refresh로 실패 행이 사라져도 보고 유지 */
        <div className="rounded-xl bg-red-50 px-4 py-3 ring-1 ring-red-200">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-red-700">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            처리하지 못한 주문
          </p>
          <div className="mt-1.5 space-y-1">
            {failReport.map((f, i) => (
              <p key={i} className="whitespace-pre-line break-keep text-xs leading-relaxed text-red-600">
                {f.nickname}: {FAIL_COPY[f.reason] ?? FAIL_COPY.error}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* 단일 부분입금 리스트(§16.9) — 정렬: ①입금 완료 처리 가능 ②확인 필요 ③진행 중(오래된 순). */}
      {groups && total > 0 && (
        <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
          <div className="divide-y divide-slate-100">
            {rows.map(({ entry: e, tier }) => (
              <div key={e.orderId} className="py-2.5 pl-3 pr-3">
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                  <button
                    type="button"
                    onClick={() => toggleDetail(e.orderId)}
                    aria-expanded={expanded.has(e.orderId)}
                    aria-controls={`partial-detail-${e.orderId}`}
                    aria-label={`${e.nickname} 주문 상세`}
                    className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${expanded.has(e.orderId) ? 'rotate-180' : ''}`} aria-hidden />
                  </button>
                  <span className="min-w-[110px] max-w-[180px] truncate text-[13px] font-semibold text-slate-900">{e.nickname}</span>
                  <span className="min-w-0 flex-1 truncate text-xs tabular-nums text-slate-400">{e.orderDate} · {summarize(e.itemNames)}</span>
                  {/* 진행률(§16.1-2): 확정 입금 / 정산서 금액 — "받음" 접미사 제거(오너 2026-07-25) */}
                  <span className={PROGRESS_PILL[tier]}>
                    {won(e.confirmedSum)} / {won(e.paidThreshold)}원
                  </span>
                  {/* 남은 금액(★오너 2026-07-25): 손님에게 바로 불러줄 숫자 — 진행률 뺄셈을 셀러가 하지 않도록.
                      remaining>0 조건이라 전액 도착 행(settleReady)은 자연히 미노출. 초과 입금 음수도 미노출. */}
                  {e.remaining > 0 && (
                    <span className="shrink-0 whitespace-nowrap rounded-full bg-amber-700 px-2 py-0.5 text-[10px] font-bold tabular-nums text-white">
                      남은 금액 {won(e.remaining)}원
                    </span>
                  )}
                  {accountLinked && tier === 'safetyLane' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0 border-slate-300 px-3 font-medium text-slate-600 shadow-none hover:border-blue-500 hover:bg-white hover:text-blue-600"
                      onClick={() => void onConvert(e)}
                      disabled={busy || error}
                    >
                      부분입금 전환
                    </Button>
                  )}
                  {accountLinked && tier === 'settleReady' && (
                    <Button
                      size="sm"
                      className="shrink-0 bg-slate-900 px-3 font-semibold text-white shadow-none hover:bg-slate-800"
                      onClick={() => void onSettle(e)}
                      disabled={busy || error}
                    >
                      입금 완료 처리
                    </Button>
                  )}
                  {accountLinked && tier === 'inProgress' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0 border-transparent px-2.5 font-medium text-slate-400 shadow-none hover:bg-slate-50 hover:text-slate-600"
                      onClick={() => void onSettle(e)}
                      disabled={busy || error}
                    >
                      입금 완료 처리
                    </Button>
                  )}
                </div>
                {/* 강조 배지(★오너): 전액 도착 = 마감 유도. up-manual이라 안 누르면 매출·재고 왜곡. */}
                {tier === 'settleReady' && (
                  <p className="mt-1 whitespace-pre-line break-keep pl-9 text-xs font-medium leading-relaxed text-emerald-700">
                    {'입금 완료됐습니다.\n입금 완료 처리 버튼을 눌러주세요.'}
                  </p>
                )}
                {/* 확인 필요 설명 한 줄(★오너): 자동 전환이 놓친 행 — 판단 기준(상품 비용 증가) 명시(플랜 2026-08-30-safety-lane-total-growth §2) */}
                {tier === 'safetyLane' && (
                  <p className="mt-1 whitespace-pre-line break-keep pl-9 text-xs leading-relaxed text-amber-700">
                    {'입금 완료 시보다 상품 비용이 늘었습니다.\n추가 구매가 맞으면 부분입금으로 바꿔 주세요.'}
                  </p>
                )}
                {/* 라이브 비교 라인(§16.1-6·§16.9): snapshot 보유 행은 항상 — equality·감소에도 표시 */}
                {e.paidTotalSnapshot !== null && (
                  <p className="mt-1 break-keep pl-9 text-xs tabular-nums leading-relaxed text-slate-500">
                    입금 완료 시 금액 {won(e.paidTotalSnapshot)}원 → 현재 금액 {won(e.totalPrice)}원
                  </p>
                )}
                {/* 주문 상세 인라인(§16.9, DepositRow.renderOrderDetail 패턴): 정산서 이동 없이 그 자리서.
                    배송비 = 정산서 금액(paidThreshold) − 상품비 — 합계가 어긋나지 않는 파생. */}
                {expanded.has(e.orderId) && (
                  <div id={`partial-detail-${e.orderId}`} className="ml-9 mt-2 rounded-lg bg-slate-50/70 px-3 py-2.5">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-lg bg-white p-2.5 text-[13px] text-slate-600 ring-1 ring-slate-200">
                        🛍 <span className="font-medium text-slate-800">{summarize(e.itemNames) || '품목 정보 없음'}</span>
                      </div>
                      <div className="rounded-lg bg-white p-2.5 text-[13px] tabular-nums ring-1 ring-slate-200">
                        <div className="flex justify-between"><span className="text-slate-500">상품 비용</span><span>{won(e.totalPrice)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">배송비</span><span>{e.paidThreshold - e.totalPrice > 0 ? won(e.paidThreshold - e.totalPrice) : '무료'}</span></div>
                        <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 font-semibold"><span>정산서 금액</span><span>{won(e.paidThreshold)}</span></div>
                        {/* 입금 현황 2줄(★오너 2026-07-25): 정산서 구성 아래에 받은 금액·남은 금액.
                            초과 입금(remaining<0)은 0으로 바닥 처리 — 받을 돈이 음수로 보이지 않도록. */}
                        <div className="mt-1.5 flex justify-between border-t border-slate-200 pt-1.5"><span className="text-slate-500">받은 금액</span><span>{won(e.confirmedSum)}</span></div>
                        <div className={`flex justify-between font-semibold ${e.remaining > 0 ? 'text-amber-700' : 'text-slate-700'}`}><span>남은 금액</span><span>{won(Math.max(0, e.remaining))}</span></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* settle 미달 확인(§16.7 실수 방지): stale 클릭·오조작이 조용히 미달 주문을 마감하는 것 차단 */}
      <AlertDialog open={settleTarget !== null} onOpenChange={(open) => { if (!open) setSettleTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>입금 완료 처리</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line break-keep">
              {settleTarget !== null &&
                `아직 남은 금액이 확인되지 않았습니다.\n받은 금액 ${won(settleTarget.confirmedSum)}원 · 정산서 금액 ${won(settleTarget.paidThreshold)}원.\n그래도 입금 완료로 처리할까요?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200 text-slate-600 hover:bg-slate-50">취소</AlertDialogCancel>
            <AlertDialogAction className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => void onSettleAck()} disabled={busy || error}>입금 완료 처리</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
